import styled from '@emotion/styled';
import { useRef, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { AdvancedFlowUtils } from '../../store/advanced-flow/AdvancedFlowUtils';
import { flowFoldersDraftStore } from '../../store/flow-folders/FlowFoldersDraftStore.ts';
import { FlowUtils } from '../../store/flow/FlowUtils';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlowsByFolderId } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useDevice } from '../../store/devices/useDevices.ts';
import { useFlowFoldersById } from '../../store/flow-folders/useFlowFolders.ts';
import { useFlowsByFolderId } from '../../store/flow/useFlows.ts';
import { useMood } from '../../store/moods/useMoods.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';

import { scrollbars } from '../../theme/elements/scrollbars';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { Glyph } from '../../components/common/Glyph';
import { ScrollContext } from '../../components/common/Scroll';
import { ChippedSearchField } from '../../components/common/search-field/ChippedSearchField';
import { dragTypes } from '../../components/dnd/dragTypes';
import { SubNavItem } from '../../components/sub-navigation/SubNavItem';
import { SubNav } from '../../components/sub-navigation/SubNavRoot';
import { TreeItem } from '../../components/tree-view/TreeItem';
import { TreeView } from '../../components/tree-view/TreeView';

import { useFlowFilter } from './FlowFilterContext';
import { FlowNavigationItemAdvancedFlowContextMenuContent } from './FlowNavigationItemAdvancedFlowContextMenuContent';
import { FlowNavigationItemFlowContextMenuContent } from './FlowNavigationItemFlowContextMenuContent';
import { FlowNavigationItemFlowFolderContextMenuContent } from './FlowNavigationItemFlowFolderContextMenuContent';
import { useAdvancedFlowdRouteId } from './useAdvancedFlowdRouteId';
import { useFlowdRouteId } from './useFlowdRouteId';
import { useFolderTree } from './useFolderTree';

export function FlowNavigation() {
  const { i18n } = useI18n();

  const location = useLocation();

  const scrollRef = useRef();

  const { advancedFlowRouteId } = useAdvancedFlowdRouteId();
  const { flowRouteId } = useFlowdRouteId();

  const {
    filterValue,
    setFilterValue,
    chipDeviceId,
    setChipDeviceId,
    chipMoodId,
    setChipMoodId,
    clearFilter,
  } = useFlowFilter();

  const [expanded, setExpanded] = useState(() => new Set([null]));

  const cardOwnersFilterSet = useMemo(() => {
    if (chipDeviceId != null) {
      return new Set([`homey:device:${chipDeviceId}`]);
    }
    if (chipMoodId != null) {
      return new Set([`homey:mood:${chipMoodId}`]);
    }
    return null;
  }, [chipDeviceId, chipMoodId]);

  // Turn a device/mood passed via route state into an active filter chip.
  useEffect(() => {
    if (location.state?.deviceId == null && location.state?.moodId == null) {
      return;
    }

    const nextRouteState = { ...location.state };

    if (location.state.deviceId != null) {
      setChipDeviceId(location.state.deviceId);
      setChipMoodId(null);
      delete nextRouteState.deviceId;
    }

    if (location.state.moodId != null) {
      setChipMoodId(location.state.moodId);
      setChipDeviceId(null);
      delete nextRouteState.moodId;
    }

    RouteManager.replaceState(nextRouteState);
  }, [location.state, setChipDeviceId, setChipMoodId]);

  const { api } = useApi();
  const { device } = useDevice({ deviceId: chipDeviceId });
  const { mood } = useMood({ moodId: chipMoodId });

  const flowFolders = useFlowFoldersById();
  const flows = useFlowsByFolderId();
  const advancedFlows = useAdvancedFlowsByFolderId();

  const flowFoldersById = flowFolders.byId;
  const flowsByFolderId = flows.byFolderId;
  const advancedFlowsByFolderId = advancedFlows.byFolderId;

  const { rootFlowFolder } = useFolderTree({
    flowFoldersById,
    flowsByFolderId,
    advancedFlowsByFolderId,
    filterValue,
    cardOwnersFilterSet,
  });

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  function handleNodeDrop({ dropTarget, dropItem }) {
    if (dropItem.type === dragTypes.FOLDER && dropTarget.type === dragTypes.FOLDER) {
      flowFoldersDraftStore.setFlowFoldersDraft({
        flowFolders: {
          [dropItem.id]: {
            ...flowFoldersById[dropItem.id],
            parent: dropTarget.id,
          },
        },
      });

      api.flow
        .updateFlowFolder({
          id: dropItem.id,
          flowfolder: {
            parent: dropTarget.id,
          },
        })
        .then(() => {
          setTimeout(() => {
            flowFoldersDraftStore.setFlowFoldersDraftDone();
            flowFoldersDraftStore.notify();
          }, 5000);
        })
        .catch(ToastManager.handleError);
    }

    if (dropItem.type === dragTypes.ITEM && dropTarget.type === dragTypes.FOLDER) {
      if (dropItem.nodeType === AdvancedFlowUtils.typeName) {
        api.flow
          .updateAdvancedFlow({ id: dropItem.id, advancedflow: { folder: dropTarget.id } })
          .catch(ToastManager.handleError);
        return;
      }

      api.flow
        .updateFlow({
          id: dropItem.id,
          flow: { folder: dropTarget.id },
        })
        .catch(ToastManager.handleError);
    }
  }

  function handleNodeSelect(event, nodeId, nodeData) {
    if (nodeData.type !== 'folder') {
      if (flowRouteId === nodeId || advancedFlowRouteId === nodeId) {
        RouteManager.toFlows();
        return;
      }

      if (AdvancedFlowUtils.isAdvancedFlow(nodeData)) {
        RouteManager.toAdvancedFlow(nodeId);
        return;
      }

      RouteManager.toFlow(nodeId);
    }
  }

  function expandNode({ nodeId }) {
    if (!expanded.has(nodeId)) {
      setExpanded(new Set([...expanded, nodeId]));
    }
  }

  function collapseNode({ nodeId }) {
    if (expanded.has(nodeId)) {
      setExpanded(new Set([...expanded].filter((id) => id !== nodeId)));
    }
  }

  return (
    <SubNav.Root>
      <SubNav.SearchFieldWrapper>
        <ChippedSearchField
          chips={
            (chipDeviceId != null && [{ key: chipDeviceId, title: device?.name ?? '?' }]) ||
            (chipMoodId != null && [{ key: chipMoodId, title: mood?.name ?? '?' }]) ||
            null
          }
          aria-label="Filter Flows"
          placeholder={i18n.messageFormatter('common.filterPlaceholder')}
          value={filterValue}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && event.target.value === '') {
              setChipDeviceId(null);
              setChipMoodId(null);
            }
          }}
          onChange={(value) => {
            setFilterValue(value);
          }}
          onRemoveChips={(keys) => {
            setChipDeviceId(null);
            setChipMoodId(null);
          }}
        />
      </SubNav.SearchFieldWrapper>

      <ScrollContext.Provider value={scrollRef}>
        <FlowNavigation.TreeView
          ref={scrollRef}
          expanded={expanded}
          selected={advancedFlowRouteId ?? flowRouteId}
          onNodeToggle={handleNodeToggle}
          onNodeSelect={handleNodeSelect}
          onNodeDrop={handleNodeDrop}
        >
          {rootFlowFolder &&
            renderFolder({
              flowFolder: rootFlowFolder,
              level: 0,
              expandNode,
              collapseNode,
              api,
              onClearFilter: clearFilter,
            })}
        </FlowNavigation.TreeView>
      </ScrollContext.Provider>
    </SubNav.Root>
  );
}

function renderFolder({ flowFolder, level, expandNode, collapseNode, api, onClearFilter }) {
  function handleSaveNameRequest(value) {
    if (flowFolder.id === 'tmp-id') {
      // Pause create store?
      api.flow
        .createFlowFolder({
          flowfolder: {
            name: value,
            parent: flowFolder.parent,
          },
        })
        .then(() => {
          // TODO
          // Notify created id to store.
          // - if update already received, update the store.
          // - if not, wait for x time for create event.
          // And flush/unpause the store.

          flowFoldersDraftStore.setFlowFoldersDraftDone();
          flowFoldersDraftStore.notify();
        })
        .catch(ToastManager.handleError);

      return;
    }

    api.flow
      .updateFlowFolder({
        id: flowFolder.id,
        flowfolder: { name: value },
      })
      .catch(ToastManager.handleError);
  }

  function handleCancelNameRequest() {
    if (flowFolder.id === 'tmp-id') {
      flowFoldersDraftStore.setFlowFoldersDraftFailed();
    }
  }

  return (
    <TreeItem
      key={flowFolder.id}
      dragType={dragTypes.FOLDER}
      level={level}
      nodeId={flowFolder.id}
      nodeData={flowFolder}
      label={flowFolder.name}
      title={flowFolder.name}
      defaultIsRenaming={flowFolder.id === 'tmp-id'}
      contextMenuContentProps={{
        flowFolder,
        expandNode,
        onClearFilter,
      }}
      contextMenuContent={FlowNavigationItemFlowFolderContextMenuContent}
      onSaveNameRequest={handleSaveNameRequest}
      onCancelNameRequest={handleCancelNameRequest}
    >
      {flowFolder.folderChildren?.map((childFlowFolder) =>
        renderFolder({
          flowFolder: childFlowFolder,
          level: level + 1,
          expandNode,
          collapseNode,
          api,
          onClearFilter,
        }),
      )}
      {flowFolder.flowChildren?.map((flow) =>
        renderFlow({
          flow,
          level: level + 1,
          expandNode,
          collapseNode,
          api,
        }),
      )}
    </TreeItem>
  );
}

function renderFlow({ flow, level, expandNode, collapseNode, api }) {
  const isAdvancedFlow = AdvancedFlowUtils.isAdvancedFlow(flow);
  const defaultIcon = isAdvancedFlow === true ? glyph.system.advancedFlow : glyph.system.flow;

  const icon =
    flow.enabled !== false
      ? defaultIcon
      : isAdvancedFlow
        ? glyph.core.advancedFlowDisabled
        : glyph.core.flowDisabled;

  function handleSaveNameRequest(value) {
    if (AdvancedFlowUtils.isAdvancedFlow(flow)) {
      api.flow
        .updateAdvancedFlow({ id: flow.id, advancedflow: { name: value } })
        .catch(ToastManager.handleError);
    } else {
      api.flow.updateFlow({ id: flow.id, flow: { name: value } }).catch(ToastManager.handleError);
    }
  }

  const iconOverride =
    flow.broken === true ? (
      <FlowNavigation.BrokenFlowIcon>
        <SubNavItem.Icon source={icon} size={theme.icon.size_default} />
        <Glyph
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
          }}
          source={glyph.core.exclamationmarkTriangleFill}
          color={theme.color.error}
          size={theme.icon.size_default}
        />
      </FlowNavigation.BrokenFlowIcon>
    ) : null;

  const opts = {};

  if (AdvancedFlowUtils.isAdvancedFlow(flow)) {
    opts.nodeType = AdvancedFlowUtils.typeName;
    opts.href = RouteManager.getPathForAdvancedFlow(flow.id);
    opts.contextMenuContent = FlowNavigationItemAdvancedFlowContextMenuContent;
  } else {
    opts.nodeType = FlowUtils.typeName;
    opts.href = RouteManager.getPathForFlow(flow.id);
    opts.contextMenuContent = FlowNavigationItemFlowContextMenuContent;
  }

  return (
    <TreeItem
      key={flow.id}
      dragType={dragTypes.ITEM}
      level={level}
      nodeId={flow.id}
      nodeType={opts.nodeType}
      nodeData={flow}
      href={opts.href}
      label={flow.name}
      title={`${flow.name} ${flow.triggerCount ?? ''}`}
      iconUrl={icon}
      iconSize={theme.icon.size_medium}
      iconOverride={iconOverride}
      isDisabled={flow.enabled === false}
      contextMenuContent={opts.contextMenuContent}
      onSaveNameRequest={handleSaveNameRequest}
    />
  );
}

// todo restore Scroll component
FlowNavigation.TreeView = styled(TreeView)`
  ${scrollbars.dark};
  flex: 1 1 0;
  min-width: 0;
  overflow-y: auto;
`;

FlowNavigation.BrokenFlowIcon = styled.div`
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;
